import yup from "../../../../../../utils/yup-extended";
import {LiveEventGenInfoFormFields} from "../../../../../../components/offering-form/live-events/gen-info/types";
import {LiveEventAttendeesFormFieldsType} from "../../../../../../components/offering-form/live-events/attendees/types";
import {LiveEventEventTypeFormFieldsType} from "../../../../../../components/offering-form/live-events/event-type/types";
import {LiveEventLocationFormFieldType} from "../../../../../../components/offering-form/live-events/location/types";
import {LiveEventSettingsFormFieldsType} from "../../../../../../components/offering-form/live-events/settings/types";
import {SessionPricingFormFieldsType} from "../../../../../../components/offering-form/session-bundle/session/pricing/types";
import {baseScalarPricingValidationSchemaFields} from "../../../../../../components/offering-form/general/pricing/validation-schema";
import {IS_PHYSICAL_LOCATION_FEATURE_ENABLED} from "../../../../../../services/config";
import {purchaseLimitValidationSchemaFields} from "../../../../../../components/offering-form/general/settings/validation-schema";

// "physical_location" is always a structurally valid value here so that an
// event created while the flag was on can still be edited/saved after the
// flag is turned off — the flag instead controls whether it can be newly
// *selected* (filtered out of the dropdown in location-select) and whether
// its fields render (gated in LiveEventLocationFormFields).
const LIVE_EVENT_CALL_LOCATIONS = [
  "video",
  "custom_url",
  "google_meet",
  "physical_location",
];

export const createLiveEventBasicFormStepFormValidationSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Event name is required")
    .typeError("Must be a valid string"),
  description: yup
    .string()
    .trim()
    .required("Event description is required")
    .typeError("Must be a valid string"),
  // coverImage: yup
  //   .string()
  //   .trim()
  //   .required("Cover image is required")
  //   .typeError("Must be a valid string"),
  callLocation: yup
    .string()
    .oneOf([...LIVE_EVENT_CALL_LOCATIONS, "zoom"], "Select a location"),
  customUrl: yup
    .string()
    .nullable()
    .when("callLocation", {
      is: (callLocation: string) => callLocation === "custom_url",
      then: (schema) => schema.required("Custom URL is required").urlWithEmoji().trim(),
      otherwise: (schema) => schema.notRequired(),
    }),
  locationAddress: yup
    .string()
    .nullable()
    .when("callLocation", {
      is: (callLocation: string) =>
        IS_PHYSICAL_LOCATION_FEATURE_ENABLED && callLocation === "physical_location",
      then: (schema) => schema.required("Venue address is required").trim(),
      otherwise: (schema) => schema.notRequired(),
    }),
  topicType: yup.string().oneOf(["single", "series"], "Select a class type"),
  startDate: yup
    .string()
    .trim()
    .when("topicType", (topicType: "single" | "series") => {
      if (topicType === "series") {
        return yup.string().trim().notRequired().nullable(true);
      }
      return yup.string().trim().required("Start date is required");
    }),
  startTime: yup
    .string()
    .trim()
    .when("topicType", (topicType: "single" | "series") => {
      if (topicType === "series") {
        return yup.string().trim().notRequired().nullable(true);
      }

      return yup.string().trim().required("Start time is required");
    }),
  duration: yup
    .string()
    .trim()
    .when("topicType", (topicType: "single" | "series") => {
      if (topicType === "series") {
        return yup.string().trim().notRequired().nullable(true);
      }

      return yup.string().trim().required("Class duration is required");
    }),
  customDuration: yup
    .number()
    .transform((parsedValue, originalValue) =>
      originalValue === "" ? null : parsedValue
    )
    .when(["duration", "topicType"], {
      is: (duration: string, topicType: "single" | "series") =>
        duration === "-1" && topicType === "single",
      then: (schema) =>
        schema
          .transform((parsedValue, originalValue) =>
            originalValue === "" ? null : parsedValue
          )
          .typeError("Must be a number")
          .min(15, "Must be 15 minutes and above")
          .max(180, "Can't be more than 180 minutes")
          .required("Custom duration is required"),
      otherwise: (schema) =>
        schema.transform((parsedValue, originalValue) =>
          originalValue === "" ? null : parsedValue
        ),
    }),
  classSeries: yup
    .array()
    .of(
      yup.object().shape({
        name: yup.string().trim().required("Name is required"),
        startDate: yup.string().trim().required("Start date is required"),
        startTime: yup.string().trim().required("Start time is required"),
        duration: yup.string().trim().required("Class duration is required"),
        customDuration: yup
          .number()
          .transform((parsedValue, originalValue) =>
            originalValue === "" ? null : parsedValue
          )
          .when(["duration", "topicType"], {
            is: (duration: string, topicType: "single" | "series") =>
              duration === "-1" && topicType === "single",
            then: (schema) =>
              schema
                .transform((parsedValue, originalValue) =>
                  originalValue === "" ? null : parsedValue
                )
                .typeError("Must be a number")
                .min(15, "Must be 15 minutes and above")
                .max(180, "Can't be more than 180 minutes")
                .required("Custom duration is required"),
            otherwise: (schema) =>
              schema.transform((parsedValue, originalValue) =>
                originalValue === "" ? null : parsedValue
              ),
          }),
      })
    )
    .when("topicType", {
      is: (topicType: "single" | "series") => topicType === "single",
      then: (schema) => schema.min(0),
      otherwise: (schema) => schema.min(1, "Add a session"),
    }),
  ...baseScalarPricingValidationSchemaFields,
  enable_currency_conversion: yup.bool().default(true).optional(),
  selected_currencies: yup.array().of(yup.string()).default([]).optional(),
  attendeesType: yup
    .string()
    .required()
    .oneOf(["unlimited", "limited"], "Select attendee limit"),
  attendees: yup.number().when("attendeesType", {
    is: (attendeesType: "unlimited" | "limited") => attendeesType === "unlimited",
    then: (schema) => schema.default(-1),
    otherwise: (schema) =>
      schema
        .min(1, "Can't be less than 1")
        .required("Number of attendees is required")
        .typeError("Must be a number"),
  }),
  hideFromProfile: yup.bool().default(false),
  requestCustomerPhoneNumber: yup.bool().default(false),
  ...purchaseLimitValidationSchemaFields,
  isUrlRedirect: yup.bool().default(false),
  redirectAfterPurchase: yup
    .mixed<"none" | "telegram" | "custom-url">()
    .oneOf(["none", "telegram", "custom-url"])
    .default("none"),
  telegramGroupId: yup.string().when("redirectAfterPurchase", {
    is: "telegram",
    then: (schema) =>
      schema
        .required("Telegram group is required")
        .test(
          "telegram-group",
          "Telegram group is required",
          (value) => value !== "none"
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  url_redirect: yup
    .string()
    .nullable()
    .when("isUrlRedirect", {
      is: (isUrlRedirect: boolean) => isUrlRedirect,
      then: (schema) => schema.required("Redirect URL is required").urlWithEmoji().trim(),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export type CreateLiveEventBasicFormStepFormValues = LiveEventGenInfoFormFields &
  LiveEventAttendeesFormFieldsType &
  LiveEventEventTypeFormFieldsType &
  LiveEventLocationFormFieldType &
  LiveEventSettingsFormFieldsType &
  SessionPricingFormFieldsType;
